// Which project database a request goes to — decided per TAB, never shared between tabs.
//
// A data page's URL names its database (/gwa01/tags), and that URL is the truth for the tab it is
// in. Requests used to read the database from localStorage, which every tab shares, so switching
// database in one tab silently pointed every other open tab at it: a tab still showing GWA saved
// into PNA. Now:
//   - On a /<slug>/ route the database is the one the URL slug names, looked up in the slug map
//     (written from the database list each time it loads).
//   - On a slug-free page (admin, dev tools) it is this tab's own database, kept in sessionStorage:
//     per tab, survives a reload, copied by Duplicate Tab.
//   - localStorage keeps only the LAST PICK — a starting point for a new tab that has no database
//     yet, and for Home / the picker. It never routes a request.
// Every storage access is wrapped: storage can be blocked, and that must not break the page.

const TAB_KEY   = 'dawn.tabDatabase';        // sessionStorage: { databaseName, slug, clientName }
const MAP_KEY   = 'dawn.dbSlugMap';          // localStorage: { slug: databaseName }
const LAST_DB   = 'pmd-selected-database';   // localStorage: last pick, any tab
const LAST_SLUG = 'pmd-selected-db-slug';
const LAST_CLIENT = 'pmd-selected-client-name';

// Routes that never carry a database slug: the pre-database screens and the slug-free pages.
const PRE_DB_PAGES = ['select-database', 'access-denied', 'accept-invitation'];
export const SLUG_FREE_PREFIXES = ['admin/', 'development-dashboard', 'sample-table-view', 'task-log', 'dev-plan', 'schema-viewer', 'field-manager', 'claude-notes', 'icon-browser', 'rev-log', 'standard-view-config', 'lookup-values', 'display-settings', 'database-sync', 'view-synchronizer', 'schema-synchronizer', 'admin-config-sync', 'style-guide', 'stui-style-guide', 'data-entry-view-config', 'ds-Master-Composer', 'navigation'];

export const isSlugFreePage = (page) => SLUG_FREE_PREFIXES.some(p => page.startsWith(p));

const readJson = (store, key) => {
  try {
    const raw = window[store].getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
const write = (store, key, value) => {
  try {
    if (value === null || value === undefined || value === '') window[store].removeItem(key);
    else window[store].setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch { /* storage blocked or full */ }
};
const readLocal = (key) => {
  try { return window.localStorage.getItem(key) || ''; } catch { return ''; }
};

const slugMap = () => readJson('localStorage', MAP_KEY) || {};

// Requests made before this page load has seen the database list wait for it (see
// waitForDatabaseList); every data page fetches the list as it mounts.
let listKnown;
const listLoaded = new Promise(resolve => { listKnown = resolve; });

/** Record slug → database name for every database the user can reach. */
export const rememberDatabaseList = (dbs) => {
  const map = {};
  (dbs || []).forEach(d => {
    const slug = (d.slug || '').toLowerCase();
    if (slug) map[slug] = d.databaseName || d.id;
  });
  write('localStorage', MAP_KEY, map);
  listKnown();
};

/** Resolves once the database list has loaded in this page, or after `ms` regardless. */
export const waitForDatabaseList = (ms = 15000) =>
  Promise.race([listLoaded, new Promise(resolve => setTimeout(resolve, ms))]);

/**
 * Where the current URL stands: { kind: 'slug', slug } on a data page whose slug is known,
 * { kind: 'unknown', slug } on what looks like a data page with a slug not in the map yet,
 * { kind: 'free' } on a slug-free or pre-database page.
 */
export const urlDatabase = () => {
  const page = (window.location.pathname || '/').replace(/^\/+/, '');
  const first = (page.split('/')[0] || '').toLowerCase();
  if (!first || PRE_DB_PAGES.includes(first) || isSlugFreePage(page)) return { kind: 'free' };
  return first in slugMap() ? { kind: 'slug', slug: first } : { kind: 'unknown', slug: first };
};

/** This tab's own database, exactly as stored — null when the tab has none yet. */
export const getTabDatabase = () => readJson('sessionStorage', TAB_KEY);

/**
 * This tab's database for a slug-free page. A tab with none yet starts from the last pick and
 * keeps it from then on, so another tab picking later does not move it.
 */
const tabDatabaseOrSeed = () => {
  const own = getTabDatabase();
  if (own) return own;
  const databaseName = readLocal(LAST_DB);
  if (!databaseName) return null;
  const seeded = { databaseName, slug: readLocal(LAST_SLUG).toLowerCase(), clientName: readLocal(LAST_CLIENT) };
  write('sessionStorage', TAB_KEY, seeded);
  return seeded;
};

/**
 * Make `db` this tab's database and the last pick. When that changes the tab's database (a new
 * tab counts), fires pmd-database-changed so anything resolved before the pick — the theme and
 * lookups a tab loads on the start-up picker, seeded from another tab's last pick — reloads.
 */
export const setTabDatabase = ({ databaseName, slug, clientName }) => {
  const entry = { databaseName: databaseName || '', slug: (slug || '').toLowerCase(), clientName: clientName || '' };
  const changed = getTabDatabase()?.databaseName !== entry.databaseName;
  write('sessionStorage', TAB_KEY, entry);
  rememberLastPick(entry);
  if (changed) window.dispatchEvent(new Event('pmd-database-changed'));
};

/** Record the last pick only (new tabs and Home start from it). Leaves this tab's database alone. */
export const rememberLastPick = ({ databaseName, slug, clientName }) => {
  if (databaseName) write('localStorage', LAST_DB, databaseName);
  if (slug) write('localStorage', LAST_SLUG, slug.toLowerCase());
  if (clientName !== undefined) write('localStorage', LAST_CLIENT, clientName);
};

/** Forget the selection everywhere: this tab, the last pick and the slug map (rebuilt on next load). */
export const clearDatabaseSelection = () => {
  write('sessionStorage', TAB_KEY, null);
  [LAST_DB, LAST_SLUG, LAST_CLIENT, MAP_KEY].forEach(k => write('localStorage', k, null));
};

/** Slug of the database this tab is working in ('' when none). */
export const activeDbSlug = () => {
  const url = urlDatabase();
  if (url.kind !== 'free') return url.slug;
  return tabDatabaseOrSeed()?.slug || '';
};

/** DatabaseName this tab is working in ('' when none). */
export const activeDbName = () => {
  const url = urlDatabase();
  if (url.kind === 'slug') return slugMap()[url.slug] || '';
  if (url.kind === 'unknown') return '';
  return tabDatabaseOrSeed()?.databaseName || '';
};

/** Client (organization) display name of this tab's database, for report file names. */
export const activeClientName = () => {
  const tab = getTabDatabase();
  return tab && tab.databaseName === activeDbName() ? tab.clientName || '' : '';
};

/**
 * The X-Database-Id a request from this tab must carry. { databaseName } to send ('' = none), or
 * { blocked } with the reason when the tab cannot tell which database it is in: a data page
 * whose slug is not in the map. Sending another database's name there is how a GWA screen ends
 * up writing into PNA, and sending none runs against the template catalog, so the caller waits
 * for the database list and refuses the request if the slug still does not resolve.
 */
export const requestDatabase = () => {
  const url = urlDatabase();
  if (url.kind === 'slug') return { databaseName: slugMap()[url.slug] };
  if (url.kind === 'unknown') return { blocked: `the database for /${url.slug}/ is not resolved yet` };
  return { databaseName: tabDatabaseOrSeed()?.databaseName || '' };
};
